import ConfirmQuantityForm from "@affinity/components/OrderQuantity/ConfirmQuantityForm";
import FullWidthRange from "@affinity/components/OrderQuantity/FullWidthRange";
import SwipableNav from "@affinity/components/SwipeNav";
import { getQuantityDescription } from "@affinity/constants/FormatHelpers";
import { colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import ServiceTracking from "@affinity/Store/ServiceTracking";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

const QuantityContainerDiv = styled.div`
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    background-color: ${colours.white};
    padding-top: 20px;
    padding-bottom: 30px;
    @media (min-width: ${breakpointWidth("lg")}) {
        padding-bottom: 40px;
    }
`;

const QuantityOutput = styled.output`
    color: ${colours.green};
    font-weight: 600;
    font-size: ${rem(30)};
    line-height: 1;
    padding: ${rem(30)} 0 ${rem(15)};
    @media (min-width: ${breakpointWidth("lg")}) {
        font-size: ${rem(40)};
    }
`;

const MinMaxDiv = styled.div`
    width: 100%;
    display: flex;
    color: ${colours.black};

    div {
        width: 50%;
        display: flex;
        flex-flow: column nowrap;
        span {
            font-size: ${rem(15)};
            line-height: 1.47;
            @media (min-width: ${breakpointWidth("lg")}) {
                font-size: ${rem(20)};
                line-height: 1.1;
            }
        }
        span:nth-child(2) {
            font-weight: 300;
            font-size: ${rem(14)};
            line-height: 1.29;
        }
    }
    div:nth-child(2) {
        align-items: flex-end;
    }
`;

const generateButtonArray = (min: number, max: number, step: number, setValue: (_: string) => void) => {
    const outArray = [];
    for (let i = min; i < max; i += step) {
        outArray.push({
            value: getQuantityDescription(i),
            onClick: () => {
                setValue(i.toString());
            },
        });
    }
    outArray.push({
        value: getQuantityDescription(max),
        onClick: () => {
            setValue(max.toString());
        },
    });
    return outArray;
};

export type OrderQuantityProps = {
    product: string;
    min: number;
    max: number;
    sliderStep: number;
    buttonStep: number;
    onClick: Function;
    showConfirmButton?: boolean;
    onConfirm?: (value: number) => void;
    value?: number;
};

const OrderQuantity = ({
    min,
    max,
    buttonStep,
    sliderStep,
    showConfirmButton,
    onClick,
    product,
    onConfirm,
    value,
}: OrderQuantityProps) => {
    const [liveValue, setLiveValue] = React.useState(value ? value.toString() : min.toString());
    const [modalOpen, setModalOpen] = React.useState(false);
    const handleConfirm = () => {
        new ServiceTracking().confirmQtyModal(); // Tracking
        setModalOpen(false);
        onClick(Number(liveValue));
        onConfirm?.(Number(liveValue));
    };
    const setValue = useCallback(
        (value: string) => {
            setLiveValue(value);
            if (!showConfirmButton) {
                onClick(Number(value));
            }
        },
        [onClick, showConfirmButton],
    );
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
        },
        [setValue],
    );
    const buttonArray = useMemo(() => generateButtonArray(min, max, buttonStep, setValue), [max]);

    return (
        <>
            <QuantityContainerDiv>
                <VisuallyHidden>
                    <label htmlFor="idForInput">Select quantity</label>
                </VisuallyHidden>
                <SwipableNav elementArray={buttonArray} />
                <VisuallyHidden>
                    <label htmlFor="idForOutput">Amount of litre's purchased</label>
                </VisuallyHidden>
                <QuantityOutput htmlFor="idForInput" id="idForOutput">
                    {getQuantityDescription(liveValue)}
                </QuantityOutput>
                <FullWidthRange
                    value={liveValue}
                    step={sliderStep}
                    min={min}
                    max={max}
                    id="idForInput"
                    onChange={handleChange}
                />
                <MinMaxDiv>
                    <div>
                        <span>{getQuantityDescription(min)}</span>
                        <span>(min)</span>
                    </div>
                    <div>
                        <span>{getQuantityDescription(max)}</span>
                        <span>(max)</span>
                    </div>
                </MinMaxDiv>
                {showConfirmButton && (
                    <Button variant="secondary" onClick={() => setModalOpen(true)}>
                        Select
                    </Button>
                )}
            </QuantityContainerDiv>
            <Modal
                headline={translate("Confirm you have adequate capacity")}
                isOpen={modalOpen}
                handleClose={() => setModalOpen(false)}
            >
                <ConfirmQuantityForm
                    onConfirm={handleConfirm}
                    onDismiss={() => setModalOpen(false)}
                    product={product.toString()}
                    amount={liveValue}
                />
            </Modal>
        </>
    );
};

export default OrderQuantity;
